/**
 * Route paths, split out of Routes.tsx so that page components can reference a
 * path without importing the module that lazy-imports them. Routes.tsx re-exports
 * this, so `import { RouteConstants } from '.../Routes'` keeps working; new code
 * inside a feature should import from here to avoid a needless import cycle.
 */
export enum RouteConstants {
    HOME = '/',
    LOGIN = '/login',
    SETTINGS_HOME = '/settings',
    TRANSLATIONS = '/translations',
    TRANSLATION_KEYS = '/translations/key',
    MANAGE_CHECKLIST = '/manage-checklist',
    MOCKUPS = '/mockups',
    MOCKUP = '/mockups/:mockupId',
    BRANDS = '/brands',
    BRAND = '/brands/:brand_id',
    JDML = '/production/jdml',
    JDML_SECTIONS = '/production/jdml/:jdmlId',
    DROPPER = '/production/dropper',
    SCANNER = '/production/scanner',
    ORDERS = '/orders',
    // Declared BEFORE ORDER so '/orders/new' is never matched as an :order_id.
    INTERNAL_ORDER_NEW = '/orders/new',
    INTERNAL_ORDER_STATUS = '/orders/internal/:order_number',
    ORDER = '/orders/:order_id',
    LINE_ITEM = '/orders/:order_id/line-item/:line_item_id',

    FAILED_ORDER = '/orders/failed/:order_queue_id',
    USERS = '/users',
    SALES_INBOX = '/sales/inbox',
    SALES_INBOX_NEXT = '/sales/inbox-next',
    SALES_INBOX_CLASSIC = '/sales/inbox-classic',
    SALES_MESSAGES = '/sales/email',
    SALES_LEAD = '/sales/leads/:leadId',
    SALES_TEAM = '/sales/team',
    SALES_CALENDAR = '/sales/calendar',
    SALES_TASKS = '/sales/tasks',
    SALES_PIPELINE = '/sales/pipeline',
    SALES_TEMPLATES = '/sales/templates',
    SALES_SIGNATURE = '/sales/signature',
    // Sales decks: the list, one deck's editor, and (outside production) the editor on the fixture deck with a mocked API.
    SALES_DECKS = '/sales/decks',
    SALES_DECK = '/sales/decks/:deckId',
    SALES_DECK_SAMPLE = '/sales/decks/__sample',
    // Public, no login: a rep's booking page and an invitee's own booking.
    BOOK = '/book/:slug',
    BOOK_TEAM = '/book/team/:slug',
    BOOK_MANAGE = '/book/manage/:token',
    BOOK_CONFIRM = '/book/confirm/:token',
    // Email marketing. Segment/template/campaign ids accept 'new' for create.
    MARKETING = '/marketing',
    MARKETING_CONTACTS = '/marketing/contacts',
    MARKETING_SEGMENTS = '/marketing/segments',
    MARKETING_SEGMENT = '/marketing/segments/:segmentId',
    MARKETING_TEMPLATES = '/marketing/templates',
    MARKETING_TEMPLATE = '/marketing/templates/:templateId',
    // Every email the platform sends by itself, edited in the same block editor (house workspace).
    MARKETING_SYSTEM_EMAILS = '/marketing/templates/system',
    MARKETING_SYSTEM_EMAIL = '/marketing/templates/system/:emailKey',
    // The brand guide (importable from a PDF) and the asset library everything is catalogued in.
    MARKETING_BRAND = '/marketing/brand',
    MARKETING_ASSETS = '/marketing/assets',
    MARKETING_CAMPAIGNS = '/marketing/campaigns',
    MARKETING_CAMPAIGN = '/marketing/campaigns/:campaignId',
    MARKETING_FLOWS = '/marketing/flows',
    MARKETING_FLOW = '/marketing/flows/:flowId',
    MARKETING_SETTINGS = '/marketing/settings',
    // What outside AI tools hand in: managers approve or send back.
    MARKETING_REVIEW = '/marketing/review',
    // Custom events (signals) and the built-in ones, with counts and how to record them.
    MARKETING_SIGNALS = '/marketing/signals',
    // Signed in, outside the dashboard: a manager lets an app (claude.ai, ChatGPT) connect through OAuth.
    MARKETING_CONNECT = '/marketing/connect',
    MARKETING_LINKS = '/marketing/links',
    MARKETING_ATTRIBUTION = '/marketing/attribution',
    // Public, no login: the preference page behind every marketing email, and double opt-in.
    EMAIL_PREFERENCES = '/email/preferences/:token',
    EMAIL_CONFIRM = '/email/confirm/:token',
    // Public, no login: the hosted deck a rep shares with a lead (?print=1 for the PDF renderer).
    DECK_VIEWER = '/d/:token',
    CUSTOMERS = '/customers',
    CUSTOMER = '/customers/:customer_id',
    WEIGH_STATION = '/production/weigh-station',
    SHIP_STATION = '/production/ship-station',
    INVOICES = '/invoices',
    INVOICE = '/invoices/:invoice_id',
    CONTACT_US = 'https://wallmates.com/contact-us',
    WASTE_TRACKING = '/waste-tracking',
    ANALYTICS = '/analytics',
    BRAND_ANALYTICS = '/brand-analytics',
    ADMIN_TOOLS = '/admin-tools',
    // Admin Tools sub-pages. Everything that used to sit loose in the SUPERADMIN
    // sidebar now lives under /admin-tools/<section> so the left admin nav stays
    // rendered — including on detail screens like a single mockup.
    ADMIN_TOOLS_DESIGN_HISTORY = '/admin-tools/design-history',
    ADMIN_TOOLS_DESIGN_HISTORY_DESIGN = '/admin-tools/design-history/:designId',
    ADMIN_TOOLS_MOCKUPS = '/admin-tools/mockups',
    ADMIN_TOOLS_MOCKUP = '/admin-tools/mockups/:mockupId',
    ADMIN_TOOLS_BRAND_ANALYTICS = '/admin-tools/brand-analytics',
    ADMIN_TOOLS_TRANSLATIONS = '/admin-tools/translations',
    ADMIN_TOOLS_TRANSLATION_KEYS = '/admin-tools/translation-keys',
    ADMIN_TOOLS_MANAGE_CHECKLIST = '/admin-tools/manage-checklist',
    ADMIN_TOOLS_FEATURE_FLAGS = '/admin-tools/feature-flags',
    ADMIN_TOOLS_ANNOUNCEMENTS = '/admin-tools/announcements',
    ADMIN_TOOLS_INVENTORY = '/admin-tools/inventory',
    ADMIN_TOOLS_INVENTORY_VENDORS = '/admin-tools/inventory/vendors',
    ADMIN_TOOLS_INVENTORY_STOCK_TYPES = '/admin-tools/inventory/stock-types',
    ADMIN_TOOLS_INVENTORY_UNIT_TYPES = '/admin-tools/inventory/unit-types',
    ADMIN_TOOLS_INVENTORY_ZONES = '/admin-tools/inventory/zones',
    ADMIN_TOOLS_INVENTORY_TRACKABLE_ITEMS = '/admin-tools/inventory/trackable-items',
    ADMIN_TOOLS_INVENTORY_TRACKABLE_ITEM = '/admin-tools/inventory/trackable-items/:trackable_item_id',
    ADMIN_TOOLS_INVENTORY_PURCHASE_ORDERS = '/admin-tools/inventory/purchase-orders',
    ADMIN_TOOLS_INVENTORY_PURCHASE_ORDER = '/admin-tools/inventory/purchase-orders/:po_id',
    ADMIN_TOOLS_INVENTORY_AUDIT_GROUPS = '/admin-tools/inventory/audit-groups',
    ADMIN_TOOLS_INVENTORY_AUDIT_GROUP = '/admin-tools/inventory/audit-groups/:group_id',
    // Inventory Management now lives in the Production nav group. The
    // /admin-tools/inventory/* paths above are retained ONLY as redirect
    // sources (see movedAdminInventoryRoutes) and must not be linked to.
    PRODUCTION_INVENTORY = '/production/inventory',
    PRODUCTION_INVENTORY_INTAKE = '/production/inventory/intake',
    PRODUCTION_INVENTORY_VENDORS = '/production/inventory/vendors',
    PRODUCTION_INVENTORY_STOCK_TYPES = '/production/inventory/stock-types',
    PRODUCTION_INVENTORY_UNIT_TYPES = '/production/inventory/unit-types',
    PRODUCTION_INVENTORY_ZONES = '/production/inventory/zones',
    PRODUCTION_INVENTORY_TRACKABLE_ITEMS = '/production/inventory/trackable-items',
    PRODUCTION_INVENTORY_TRACKABLE_ITEM = '/production/inventory/trackable-items/:trackable_item_id',
    PRODUCTION_INVENTORY_PURCHASE_ORDERS = '/production/inventory/purchase-orders',
    PRODUCTION_INVENTORY_PURCHASE_ORDER = '/production/inventory/purchase-orders/:po_id',
    PRODUCTION_INVENTORY_AUDIT_GROUPS = '/production/inventory/audit-groups',
    PRODUCTION_INVENTORY_AUDIT_GROUP = '/production/inventory/audit-groups/:group_id',
    ADMIN_TOOLS_JDML = '/admin-tools/jdml',
    ADMIN_TOOLS_JDML_SECTIONS = '/admin-tools/jdml/:jdmlId',
    ADMIN_TOOLS_DROPPER = '/admin-tools/dropper',
    ADMIN_TOOLS_ANALYTICS = '/admin-tools/analytics',
    ADMIN_TOOLS_CANCELLATIONS = '/admin-tools/cancellations',
    FEATURE_FLAGS = '/development/feature-flags',
    ANNOUNCEMENTS = '/development/announcements',
    // Inventory Management
    INVENTORY_HOME = '/inventory',
    INVENTORY_VENDORS = '/inventory/vendors',
    INVENTORY_STOCK_TYPES = '/inventory/stock-types',
    INVENTORY_UNIT_TYPES = '/inventory/unit-types',
    INVENTORY_ZONES = '/inventory/zones',
    INVENTORY_TRACKABLE_ITEMS = '/inventory/trackable-items',
    INVENTORY_TRACKABLE_ITEM = '/inventory/trackable-items/:trackable_item_id',
    INVENTORY_PURCHASE_ORDERS = '/inventory/purchase-orders',
    INVENTORY_PURCHASE_ORDER = '/inventory/purchase-orders/:po_id',
    INVENTORY_AUDIT_GROUPS = '/inventory/audit-groups',
    INVENTORY_AUDIT_GROUP = '/inventory/audit-groups/:group_id',
    DESIGN_CREATION = '/design-creation',
    DESIGNER_CATALOG = '/designer-catalog',
    INSPO = '/inspo',
    SAMPLE_BUNDLES = '/sample-bundles',
    CREATE_SAMPLE_BUNDLE = '/sample-bundles/new',
    PRODUCT_LISTING = '/product-listing',
    LICENSED_DESIGNS = '/product-listing/licensed',
    LICENSING_SETUP = '/product-listing/setup',
    PRODUCT_LISTING_BRAND = '/product-listing/brand/:brandId',
    INTERIOR_PROJECTS = '/interior-projects',
    INTERIOR_PROJECT_DETAIL = '/interior-projects/:projectId',
    INTERIOR_BRANDING = '/interior-branding',
    DESIGN_CREATION_DESIGN = '/design-creation/design/:designId',
    DESIGN_CREATION_MOCKUPS = '/design-creation/design/:designId/mockups',
    EARNINGS = '/earnings',
    // Signed-in pricing (Brand vs Artist). The public, embeddable page is /pricing.
    PRICING_GUIDE = '/pricing-guide',
    CANCELLATIONS = '/production/cancellations',
    RELEASE_NOTES = '/release-notes',
    MATERIAL_GUIDE = '/material-guide',
    API_DOCS = '/api-docs',
    INTERIOR_SHARE = '/share/interior/:token',
    ANY = '*'
}
